import { validateValueHasOnlyNumericCharacters } from '~/_shared/utils/form/form.helpers';
import {
  limitValueToMaxPossibleLength, MAX_FRACTIONAL_PART_LENGTH,
} from '~/_shared/utils/number/number.helpers';
import {
  type NumericalRange,
  recalculateRanges,
} from '~/_shared/utils/range/range.helpers';
import { isTextEmpty } from '~/_shared/utils/text/text.helpers';
import { isNullsy } from '~/_shared/utils/typeGuards';
import { type BoundaryTerritory } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import {
  BOUNDARY_MAX_VALUE_ID,
  BOUNDARY_MIN_VALUE_ID,
  BOUNDARY_VALUES_SEPARATOR,
  isBoundaryTerritorySpecialOrCustom,
} from '../boundarySettings.helpers';

type BoundaryTerritoryValues = {
  valueFrom: string | typeof BOUNDARY_MIN_VALUE_ID;
  valueTo: string | typeof BOUNDARY_MAX_VALUE_ID;
};

export const getBoundaryMaxRange = (min: number | typeof BOUNDARY_MIN_VALUE_ID, max: number | typeof BOUNDARY_MAX_VALUE_ID): number => {
  if (min === BOUNDARY_MIN_VALUE_ID || max === BOUNDARY_MAX_VALUE_ID) {
    return 10;
  }

  const rangesCount = max - min + 1;

  if (rangesCount < 10) {
    return rangesCount;
  }
  else {
    return 10;
  }
};

export const handleBoundaryTerritoryValueToChange = (
  index: number,
  valueTo: string,
  dataRows: ReadonlyArray<BoundaryTerritory>,
  min: number | typeof BOUNDARY_MIN_VALUE_ID,
  max: number | typeof BOUNDARY_MAX_VALUE_ID,
  isDecimal: boolean
): ReadonlyArray<BoundaryTerritory> => {
  if (!validateValueHasOnlyNumericCharacters(valueTo, { isDecimal })) {
    return dataRows;
  }

  const currentBoundaryTerritory = dataRows[index];
  if (!currentBoundaryTerritory) {
    return dataRows;
  }

  const currentBoundaryValues = getBoundaryTerritoryRangeValues(currentBoundaryTerritory);

  if (!currentBoundaryValues) {
    return dataRows;
  }

  const parsedValueTo: '' | number = isTextEmpty(valueTo) ? '' : +valueTo;

  if (parsedValueTo === '' || isNaN(parsedValueTo) || valueTo.endsWith('.')) {
    const newRowData = {
      ...currentBoundaryTerritory,
      bucket: getBoundaryTerritoryBucketFromValues({
        valueFrom: currentBoundaryValues.valueFrom,
        valueTo,
      }),
    };

    const newDataRows = [...dataRows];
    newDataRows[index] = newRowData;

    return newDataRows;
  }

  valueTo = limitValueToMaxPossibleLength(valueTo);

  const numericalDataRows = dataRows.filter(row => !isBoundaryTerritorySpecialOrCustom(row));
  const specialDataRows = dataRows.filter(row => isBoundaryTerritorySpecialOrCustom(row));

  const newNumericalDataRows = recalculateDataRowsForNewValueTo(numericalDataRows, index, valueTo, min, max, isDecimal);

  return newNumericalDataRows.concat(specialDataRows);
};

export const recalculateBoundaryTerritoriesValues = (
  dataRows: ReadonlyArray<BoundaryTerritory>,
  newMin: number,
  newMax: number,
  prevMin: number,
  prevMax: number,
  isDecimalMode: boolean
): BoundaryTerritory[] => {
  // get rows with valid values
  const territoriesWithValues = dataRows.filter(row => !!getBoundaryTerritoryRangeValues(row));

  // get rows with null values (meaning these are special rows)
  const nonRangeTerritories = dataRows.filter(row => !getBoundaryTerritoryRangeValues(row));

  const ranges: NumericalRange[] = territoriesWithValues.map(territory => {
    const values = getBoundaryTerritoryRangeValues(territory);
    if (!values) {
      throw Error('Missing territory range values');
    }

    return {
      from: values.valueFrom === BOUNDARY_MIN_VALUE_ID ? prevMin : +values.valueFrom,
      to: values.valueTo === BOUNDARY_MAX_VALUE_ID ? prevMax : +values.valueTo,
    };
  });

  const newRanges = recalculateRanges(ranges, newMin, newMax, isDecimalMode);

  // recalculate ranges for valid values rows
  const recalculatedBoundariesWithValues: BoundaryTerritory[] = territoriesWithValues.map((row, index) => {
    const boundaryValues = getBoundaryTerritoryRangeValues(row);
    const newRange = newRanges[index];

    if (!boundaryValues || !newRange) {
      throw Error(`Missing territory range values: boundaryValues: '${boundaryValues}, newRange: ${newRange}.`);
    }

    const valueFrom: number = newRange.from;
    const valueTo: number = newRange.to;

    return {
      ...row,
      bucket: getBoundaryTerritoryBucketFromValues({
        valueFrom: valueFrom === newMin ? BOUNDARY_MIN_VALUE_ID : valueFrom.toString(),
        valueTo: valueTo === newMax ? BOUNDARY_MAX_VALUE_ID : valueTo.toString(),
      }),
    };
  });

  return recalculatedBoundariesWithValues.concat(nonRangeTerritories);
};

export const recalculateDataRowsForNewValueTo = (
  dataRows: ReadonlyArray<BoundaryTerritory>,
  index: number,
  newToValue: string,
  rangeMin: number | typeof BOUNDARY_MIN_VALUE_ID,
  rangeMax: number | typeof BOUNDARY_MAX_VALUE_ID,
  isDecimal: boolean
): ReadonlyArray<BoundaryTerritory> => {
  const dataRow = dataRows[index];
  const boundaryValues = getBoundaryTerritoryRangeValues(dataRow);

  if (!dataRow || dataRow.custom || !boundaryValues) {
    return dataRows;
  }

  const newDataRows: BoundaryTerritory[] = [...dataRows];
  newDataRows[index] = {
    ...dataRow,
    bucket: getBoundaryTerritoryBucketFromValues({
      valueFrom: boundaryValues.valueFrom,
      valueTo: newToValue,
    }),
  };

  const parsedValueTo = +newToValue;
  const valueFrom = boundaryValues.valueFrom === BOUNDARY_MIN_VALUE_ID ? BOUNDARY_MIN_VALUE_ID : +boundaryValues.valueFrom;

  if (isTextEmpty(newToValue) || isNaN(parsedValueTo) || !validateBoundaryValue(valueFrom, parsedValueTo)) {
    return newDataRows;
  }

  // the last range row (ideally the one ending at max) anchors the redistribution of all following ranges
  let anchorIndex: number | null = null;
  let anchorValue: number | null = null;

  for (let i = index + 1; i < dataRows.length; i++) {
    const values = getBoundaryTerritoryRangeValues(dataRows[i]);
    if (!values) {
      break;
    }

    anchorIndex = i;

    if (values.valueTo === BOUNDARY_MAX_VALUE_ID) {
      anchorValue = rangeMax !== BOUNDARY_MAX_VALUE_ID ? rangeMax : getMaxRangeValue(dataRows, parsedValueTo, rangeMin);
      break;
    }

    anchorValue = +values.valueTo;
  }

  if (anchorIndex === null) {
    return newDataRows;
  }

  const slicesCount = anchorIndex - index;
  const innerBounds = anchorValue === null || isNaN(anchorValue) ?
    null :
    getEquallyDistributedBounds(parsedValueTo, anchorValue, slicesCount, isDecimal)
      ?? getTightlyPackedBounds(parsedValueTo, anchorValue, slicesCount, isDecimal);

  // when there is no room left for the following ranges at all, they are collapsed to zero width
  const newBounds = [newToValue, ...(innerBounds ?? Array<string>(slicesCount - 1).fill(newToValue))];

  for (let i = index + 1; i <= anchorIndex; i++) {
    const currentDataRow = newDataRows[i];
    const values = getBoundaryTerritoryRangeValues(currentDataRow);
    const newValueFrom = newBounds[i - index - 1];
    const newValueTo = i === anchorIndex ? values?.valueTo : newBounds[i - index];

    if (!currentDataRow || currentDataRow.custom || !values || newValueFrom === undefined || newValueTo === undefined) {
      continue;
    }

    newDataRows[i] = {
      ...currentDataRow,
      bucket: getBoundaryTerritoryBucketFromValues({
        valueFrom: newValueFrom,
        valueTo: newValueTo,
      }),
    };
  }

  return newDataRows;
};

const getMaxRangeValue = (
  dataRows: ReadonlyArray<BoundaryTerritory>,
  newValueTo: number,
  rangeMin: number | typeof BOUNDARY_MIN_VALUE_ID,
): number => {
  let max = rangeMin === BOUNDARY_MIN_VALUE_ID ? newValueTo : Math.max(rangeMin, newValueTo);

  for (const row of dataRows) {
    const values = getBoundaryTerritoryRangeValues(row);
    for (const value of [values?.valueFrom, values?.valueTo]) {
      if (value !== undefined && !isTextEmpty(value) && !isNaN(+value)) {
        max = Math.max(max, +value);
      }
    }
  }

  return max;
};

const formatBoundValue = (value: number, decimalPlaces: number): string =>
  value.toFixed(decimalPlaces).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');

// Splits (from, to) into equally wide slices and returns the inner bounds rounded to the lowest precision
// (whole numbers if possible) that keeps every slice non-empty. Returns null if no such precision exists.
export const getEquallyDistributedBounds = (
  from: number,
  to: number,
  slicesCount: number,
  isDecimal: boolean,
): string[] | null => {
  if (slicesCount <= 1) {
    return [];
  }

  if (to <= from) {
    return null;
  }

  const maxDecimalPlaces = isDecimal ? MAX_FRACTIONAL_PART_LENGTH : 0;
  const sliceWidth = (to - from) / slicesCount;

  for (let decimalPlaces = 0; decimalPlaces <= maxDecimalPlaces; decimalPlaces++) {
    const bounds: number[] = [];

    for (let i = 1; i < slicesCount; i++) {
      bounds.push(+(from + sliceWidth * i).toFixed(decimalPlaces));
    }

    const isStrictlyIncreasing = [from, ...bounds, to].every((bound, i, all) => i === 0 || bound > (all[i - 1] ?? bound));

    if (isStrictlyIncreasing) {
      return bounds.map(bound => formatBoundValue(bound, decimalPlaces));
    }
  }

  return null;
};

// Fallback for when (from, to) is too narrow to give every slice a non-empty width. Fills the leading slices
// with the smallest allowed step (1 or the finest decimal) and collapses the remaining ones at the last bound that fits.
// Returns null if there is no room at all.
export const getTightlyPackedBounds = (
  from: number,
  to: number,
  slicesCount: number,
  isDecimal: boolean,
): string[] | null => {
  if (slicesCount <= 1) {
    return [];
  }

  if (to <= from) {
    return null;
  }

  const decimalPlaces = isDecimal ? MAX_FRACTIONAL_PART_LENGTH : 0;
  const scale = Math.pow(10, decimalPlaces);
  const lastBound = Math.max(from, Math.floor(to * scale) / scale);
  const bounds: string[] = [];

  for (let i = 1; i < slicesCount; i++) {
    const bound = Math.min(+(from + i / scale).toFixed(decimalPlaces), lastBound);
    bounds.push(formatBoundValue(bound, decimalPlaces));
  }

  return bounds;
};

export const getBoundaryTerritoryRangeValues = (territory: Maybe<BoundaryTerritory>): BoundaryTerritoryValues | null => {
  if (!territory || territory.custom) {
    return null;
  }

  const [valueFrom, valueTo] = territory.bucket.split(BOUNDARY_VALUES_SEPARATOR);
  if (isNullsy(valueFrom) || isNullsy(valueTo) || isBoundaryTerritorySpecialOrCustom(territory)) {
    return null;
  }

  return {
    valueFrom,
    valueTo,
  };
};

export const getBoundaryTerritoryBucketFromValues = (values: BoundaryTerritoryValues): string => {
  return `${values.valueFrom}${BOUNDARY_VALUES_SEPARATOR}${values.valueTo}`;
};

export const validateBoundarySettingsNumericValue = (
  territory: BoundaryTerritory,
  previousTerritory: BoundaryTerritory | null,
  nextTerritory: BoundaryTerritory | null,
): boolean => {
  if (isBoundaryTerritorySpecialOrCustom(territory)) {
    return true;
  }

  const territoryValues = getBoundaryTerritoryRangeValues(territory);

  if (!territoryValues || territoryValues.valueTo === BOUNDARY_MAX_VALUE_ID) {
    return true;
  }

  // make sure there is any value and there are no dots at the end of the valueTo
  if (territoryValues.valueTo === '' || /\.+$/.test(territoryValues.valueTo)) {
    return false;
  }

  const parsedValueTo = +territoryValues.valueTo;
  const parsedValueFrom: typeof BOUNDARY_MIN_VALUE_ID | number =
    territoryValues.valueFrom === BOUNDARY_MIN_VALUE_ID ? BOUNDARY_MIN_VALUE_ID : +territoryValues.valueFrom;

  if (isNaN(parsedValueTo)) {
    return false;
  }

  if (parsedValueFrom !== BOUNDARY_MIN_VALUE_ID && parsedValueTo < parsedValueFrom) {
    return false;
  }

  if (previousTerritory) {
    if (isBoundaryTerritorySpecialOrCustom(previousTerritory) || territoryValues.valueFrom === BOUNDARY_MIN_VALUE_ID) {
      // all is good in this case
    }
    else {
      const previousTerritoryValues = getBoundaryTerritoryRangeValues(previousTerritory);
      const previousValueTo = parseBoundaryValue(previousTerritoryValues?.valueTo);
      const previousValueFrom = parseBoundaryValue(previousTerritoryValues?.valueFrom);

      if (previousValueTo !== null && +territoryValues.valueFrom < previousValueTo) {
        return false;
      }

      if (previousValueFrom !== null && +territoryValues.valueFrom < previousValueFrom) {
        return false;
      }
    }
  }

  if (nextTerritory) {
    if (isBoundaryTerritorySpecialOrCustom(nextTerritory) || territoryValues.valueTo === BOUNDARY_MAX_VALUE_ID) {
      // all is good in this case
    }
    else {
      const nextTerritoryValues = getBoundaryTerritoryRangeValues(nextTerritory);
      const nextValueFrom = parseBoundaryValue(nextTerritoryValues?.valueFrom);
      const nextValueTo = parseBoundaryValue(nextTerritoryValues?.valueTo);

      if (nextValueFrom !== null && parsedValueTo > nextValueFrom) {
        return false;
      }

      if (nextValueTo !== null && parsedValueTo > nextValueTo) {
        return false;
      }
    }
  }

  return true;
};

// neighbour values that are empty (being edited) or not numeric (min/max) must not be compared, as +'' coerces to 0
const parseBoundaryValue = (value: string | undefined): number | null =>
  value === undefined || isTextEmpty(value) || isNaN(+value) ? null : +value;

export const areBoundaryTerritoriesNumericValuesValid = (territories: ReadonlyArray<BoundaryTerritory>): boolean =>
  territories.every((territory, index) =>
    validateBoundarySettingsNumericValue(territory, territories[index - 1] ?? null, territories[index + 1] ?? null));

// scope's min/max are informational only (the same buckets are reused across scopes with different data ranges), so only row order is enforced here
const validateBoundaryValue = (
  valueFrom: number | typeof BOUNDARY_MIN_VALUE_ID,
  valueTo: number,
): boolean => {
  return valueFrom === BOUNDARY_MIN_VALUE_ID || valueTo >= valueFrom;
};
